import { useCallback, useEffect, useState } from "react";
import { OctagonX, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Panel, Spinner } from "@/components/common";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export const EmergencyPanel = ({ onDone }) => {
  const [s, setS] = useState(null);
  const [ask, setAsk] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/emergency").then((r) => setS(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    const action = ask;
    setAsk("");
    setBusy(true);
    try {
      const { data } = await api.post(`/emergency/${action}`);
      toast.success(action === "pause-all" ? `Paused ${data.emergency.campaigns.length} campaigns` : `Resumed ${data.resumed.length} campaigns`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); load(); }
  };
  if (!s) return null;
  const active = s.emergency?.active;
  return (
    <Panel title="Emergency" icon={OctagonX} testid="emergency-panel">
      {active ? (
        <>
          <p className="mb-3 text-sm text-red-300" data-testid="emergency-active">Paused {s.emergency.campaigns.length} campaigns on {fmtDate(s.emergency.at)}{s.emergency.by === "watchdog" ? " (spend watchdog)" : ""}: {s.emergency.campaigns.map((c) => c.campaign_name).join(", ")}</p>
          <button data-testid="resume-all-button" disabled={busy} onClick={() => setAsk("resume-all")} className="cc-btn cc-btn-primary w-full justify-center py-3">{busy ? <Spinner className="h-4 w-4" /> : <Play className="h-4 w-4" />} Resume all</button>
        </>
      ) : (
        <button data-testid="pause-all-button" disabled={busy} onClick={() => setAsk("pause-all")} className="w-full rounded-md bg-red-600 py-4 font-display text-lg font-bold text-white transition-colors hover:bg-red-500 disabled:opacity-50">{busy ? "Pausing…" : "PAUSE ALL CAMPAIGNS"}</button>
      )}
      {!s.execution_enabled && <p className="mt-2 text-xs text-amber-300" data-testid="emergency-exec-off">Live execution is OFF: pausing needs it ON (Approvals page).</p>}
      <AlertDialog open={!!ask} onOpenChange={(o) => !o && setAsk("")}>
        <AlertDialogContent data-testid="emergency-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{ask === "pause-all" ? "Pause every enabled campaign?" : "Resume the paused campaigns?"}</AlertDialogTitle>
            <AlertDialogDescription>{ask === "pause-all" ? "All ENABLED campaigns stop serving now. Their previous state is recorded so 'Resume all' restores exactly those campaigns." : "Only the campaigns paused by Emergency pause are re-enabled (budget cap still applies)."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="emergency-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="emergency-confirm" onClick={run}>{ask === "pause-all" ? "Pause all" : "Resume all"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
